"use client"

import { Head, router } from "@inertiajs/react"
import { LinkExternal01, Plus, SearchLg } from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"

import {
  type DestinationRow,
  DestinationsTable,
} from "@/components/admin/onwards/destinations-table"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import { PaginationCardMinimal } from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import {
  adminOnwardsDestinationsPath,
  newAdminOnwardsDestinationPath,
} from "@/routes"
import type { Pagination } from "@/types/contents/pagination"

interface Filters {
  q: string | null
  kind: string
  status: string
}

interface IndexProps {
  destinations: DestinationRow[]
  pagination: Pagination
  filters: Filters
  kinds: string[]
}

export default function DestinationsIndex({
  destinations,
  pagination,
  filters,
  kinds,
}: IndexProps) {
  const [query, setQuery] = useState(filters.q ?? "")

  const visit = (params: Partial<Filters> & { page?: number }) => {
    router.get(
      adminOnwardsDestinationsPath(),
      {
        q: params.q ?? filters.q ?? undefined,
        kind: params.kind ?? filters.kind,
        status: params.status ?? filters.status,
        page: params.page ?? 1,
      },
      { preserveState: true, replace: true },
    )
  }

  // Debounced search — fires only once typing pauses, and never on mount.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeout = setTimeout(() => visit({ q: query || undefined }), 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const kindItems = [
    { id: "all", label: "All kinds" },
    ...kinds.map((k) => ({ id: k, label: k.replace("_", " ") })),
  ]
  const statusItems = [
    { id: "all", label: "All statuses" },
    { id: "draft", label: "Draft" },
    { id: "validated", label: "Validated" },
    { id: "archived", label: "Archived" },
  ]

  const isFiltered =
    Boolean(filters.q) || filters.kind !== "all" || filters.status !== "all"

  return (
    <DashboardLayout>
      <Head title="Onward destinations" />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
              Onward destinations
            </h1>
            <p className="text-md text-tertiary">
              Support services you can place on a playlist. Only validated ones
              show up in an outro.
            </p>
          </div>
          <Button
            size="sm"
            iconLeading={Plus}
            onClick={() => router.visit(newAdminOnwardsDestinationPath())}
          >
            New destination
          </Button>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            size="sm"
            icon={SearchLg}
            aria-label="Search destinations"
            placeholder="Search by name or key"
            value={query}
            onChange={setQuery}
            className="sm:max-w-xs"
          />
          <div className="flex gap-3">
            <Select
              size="sm"
              aria-label="Filter by kind"
              items={kindItems}
              selectedKey={filters.kind}
              onChange={(key) => key != null && visit({ kind: String(key) })}
              className="w-44 capitalize"
            >
              {(item) => (
                <Select.Item key={item.id} id={item.id}>
                  {item.label}
                </Select.Item>
              )}
            </Select>
            <Select
              size="sm"
              aria-label="Filter by status"
              items={statusItems}
              selectedKey={filters.status}
              onChange={(key) => key != null && visit({ status: String(key) })}
              className="w-40"
            >
              {(item) => (
                <Select.Item key={item.id} id={item.id}>
                  {item.label}
                </Select.Item>
              )}
            </Select>
          </div>
        </div>

        {destinations.length === 0 ? (
          <EmptyState size="sm" className="py-10">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon
                color="gray"
                theme="modern"
                icon={LinkExternal01}
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <p className="text-primary text-md text-center font-semibold">
                {isFiltered ? "No destinations match" : "No destinations yet"}
              </p>
              <EmptyState.Description>
                {isFiltered
                  ? "Try a different search or clear the filters."
                  : "Add a support service to place on playlists."}
              </EmptyState.Description>
            </EmptyState.Content>
          </EmptyState>
        ) : (
          <DestinationsTable
            rows={destinations}
            aria-label="Onward destinations"
            footer={
              pagination.total > 1 ? (
                <PaginationCardMinimal
                  page={pagination.page}
                  total={pagination.total}
                  onPageChange={(page) => visit({ page })}
                />
              ) : undefined
            }
          />
        )}
      </main>
    </DashboardLayout>
  )
}
